import React from 'react';
import { PerformanceDatapoint, logUsage } from 'owa-analytics';
import {
    type ControlInMenuRendererFunction,
    type ControlRendererFunction,
    type RibbonTabDefinitionMLR,
    type RibbonTabDefinitionSet,
    type RibbonTabDefinitionSLR,
} from 'owa-acui';
import {
    addComponentEndRenderingTiming,
    addComponentStartRenderingTiming,
} from 'owa-analytics-shared';
import {
    getIsViewModeSelected,
    getRibbonViewMode,
    getLocalKeytipsEnabledStatus,
} from 'owa-command-ribbon-store';
import type { AppPaneUnderlayViewState } from 'owa-app-pane-underlay';
import { CommandRibbonBottomBar } from './CommandRibbonBottomBar';
import { CommandRibbonTopBar } from './CommandRibbonTopBar';
import { type CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import { type DatapointVariant } from 'owa-analytics-types';
import type { GovernedTask } from 'owa-tti/lib/types/GovernedTask';
import type { IconColorMap } from '../util/getRibbonIconColor';
import type { RibbonId } from 'owa-ribbon-ids';
import { RibbonKeytipLayer } from './RibbonKeytipLayer/RibbonKeytipLayer';
import { getStringRibbonId } from 'owa-ribbon-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyGovern } from 'owa-tti';
import { observer } from 'owa-mobx-react';
import { RibbonContexts } from './RibbonContexts/RibbonContexts';
import { RibbonRoot } from './RibbonRoot/RibbonRoot';
import { RibbonTheme } from './RibbonTheme/RibbonTheme';
import { getSelectedTab } from '../util/getSelectedTab';

export interface CommandRibbonProps {
    getSingleLineRibbonDefinition: () => RibbonTabDefinitionSet;
    getMultiLineRibbonDefinition: () => RibbonTabDefinitionSet;
    iconColorMap: IconColorMap;
    ribbonScenarioName: string;
    controlRendererFunction: (c: ControlInMenuRendererFunction) => ControlRendererFunction;
    getDefaultSelectedTabId?: (targetWindow: Window) => RibbonId;
    onTabClick?: (tabId: string, targetWindow: Window) => void;
    getHideTopRibbon?: boolean;
    getExtraTopRibbonControlsBeforeTabs?: () => JSX.Element;
    getExtraTopRibbonControlsAfterTabs?: () => JSX.Element;
    copilotCommandCenter?: React.ReactNode;
    thisWindow?: Window;
    disableAnimation?: boolean;
    /** Prevents modal dialogs from adding a second keyTipLayer */
    noKeytipLayer?: boolean;
    /** Array of tasks to be lazyGovern'd on React.useEffect() */
    governedTasks?: GovernedTask[];
    projectionUnderlay?: AppPaneUnderlayViewState;
    showRibbonModeToggle?: boolean;
    isInDialog?: boolean;
}

export const CommandRibbon: React.FC<CommandRibbonProps> = observer(props => {
    React.useEffect(() => {
        if (props.governedTasks && props.governedTasks.length > 0) {
            lazyGovern.importAndExecute(...props.governedTasks);
        }
        // Log initial ribbon preference and only log it once
        logUsage('RibbonPreference', { ribbonType: getRibbonViewMode() }, { variant: 2 });
    }, []);

    const renderCount = React.useRef<number>(0);
    let perfDatapoint: PerformanceDatapoint | undefined = undefined;

    React.useEffect(() => {
        renderCount.current++;
    });

    const shouldLogPerf =
        isFeatureEnabled('mon-ribbon-telemetry') && renderCount.current % 10 === 0;

    if (shouldLogPerf) {
        perfDatapoint = new PerformanceDatapoint('CommandRibbon', {
            sessionSampleRate: 1, // sample at 1% as ribbon re-renders quite often
        });
        addComponentStartRenderingTiming(perfDatapoint);
    }

    const targetWindow: Window = props.thisWindow ?? window;
    const defaultSelectedTabId = props.getDefaultSelectedTabId
        ? getStringRibbonId(props.getDefaultSelectedTabId(targetWindow))
        : '';
    const [selectedTabId, setSelectedTabId] = React.useState(defaultSelectedTabId);

    const isSinglelineMode: boolean = getIsViewModeSelected(1);

    const isProjectionPopout: boolean = targetWindow !== window;

    if (shouldLogPerf && perfDatapoint) {
        perfDatapoint.addCustomData({
            FirstRender: renderCount.current === 0,
            Scenario: props.ribbonScenarioName,
            SingleLine: isSinglelineMode,
        });
    }

    React.useEffect(() => {
        setSelectedTabId(defaultSelectedTabId);
    }, [defaultSelectedTabId]);

    const onTabHeaderClick = React.useCallback(
        (tabId: string) => {
            if (props.onTabClick) {
                props.onTabClick(tabId, targetWindow);
            }

            setSelectedTabId(tabId);
        },
        [props.onTabClick]
    );

    const { tabs }: RibbonTabDefinitionSet = isSinglelineMode
        ? props.getSingleLineRibbonDefinition()
        : props.getMultiLineRibbonDefinition();

    const selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR = getSelectedTab(
        tabs,
        selectedTabId,
        defaultSelectedTabId,
        onTabHeaderClick
    );

    if (shouldLogPerf && perfDatapoint) {
        // Call the function to log the initial timing
        addComponentEndRenderingTiming(perfDatapoint as PerformanceDatapoint);
    }

    return (
        <RibbonTheme iconColorMap={props.iconColorMap}>
            <RibbonRoot isInDialog={props.isInDialog}>
                <RibbonContexts appWindow={targetWindow}>
                    {!props.getHideTopRibbon && (
                        <CommandRibbonTopBar
                            extraControlsBeforeTabs={props.getExtraTopRibbonControlsBeforeTabs?.()}
                            extraControlsAfterTabs={props.getExtraTopRibbonControlsAfterTabs?.()}
                            onTabHeaderClick={onTabHeaderClick}
                            ribbonTabs={tabs}
                            selectedTab={selectedTab}
                        />
                    )}

                    <CommandRibbonBottomBar
                        disableAnimation={props.disableAnimation}
                        isProjectionPopout={isProjectionPopout}
                        projectionUnderlay={props.projectionUnderlay}
                        selectedTab={selectedTab}
                        controlRendererFunction={props.controlRendererFunction}
                        copilotCommandCenter={props.copilotCommandCenter}
                        showRibbonModeToggle={props.showRibbonModeToggle}
                    />
                </RibbonContexts>

                <Keytips
                    inModal={!!props.noKeytipLayer}
                    inPopout={isProjectionPopout}
                    selectedTabId={selectedTabId}
                />
            </RibbonRoot>
        </RibbonTheme>
    );
}, 'CommandRibbon');

/** Wrapping `RibbonKeytipLayer` with logic to determine when to render */
export interface KeytipsProps {
    inModal: boolean;
    inPopout: boolean;
    selectedTabId: string;
}
const Keytips = observer(({ inModal, inPopout, selectedTabId }: KeytipsProps) => {
    if (inModal || inPopout || getLocalKeytipsEnabledStatus() === false) {
        return null;
    }

    return <RibbonKeytipLayer selectedTabId={selectedTabId} />;
}, 'Keytips');
